import { Add, Close, Delete, Edit } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useAlert } from '../../context/AlertContext';
import { useCreateCourtRole, useDeleteCourtRole } from '../../Aemter/hooks';
import { useUpdateCourtRole } from '../../hooks/reactQuerys/CourtRoleQueries';
import type { ICourtRole } from '../../Interfaces/ICourtRole';

interface IProps {
  courtRoles: ICourtRole[];
  onClose: () => void;
  onChange: () => void;
}

/**
 * Stammdaten-Dialog für Gerichtsrollen (Grundbuchamt, Finanzamt, Amtsgericht …).
 *
 * Bewusst als Dialog statt eigene Seite — Rollen-Pflege ist seltene
 * Stammdaten-Arbeit, die im Workflow der Workbench unterbrochen wird, aber
 * sofort wieder verlassen werden soll. Inline-Rename + Inline-Delete halten
 * den Pfad kurz; Anlage neuer Rollen über das obere Eingabefeld + Enter.
 */
export const CourtRolesManager: React.FC<IProps> = ({
  courtRoles,
  onClose,
  onChange,
}) => {
  const { showAlert } = useAlert();
  const { createCourtRole, isPendingCreateCourtRole } = useCreateCourtRole();
  const { deleteCourtRole, isPendingDeleteCourtRole } = useDeleteCourtRole();
  const { updateCourtRole, isPendingUpdateCourtRole } = useUpdateCourtRole();

  const [search, setSearch] = useState('');
  const [newRoleName, setNewRoleName] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState('');

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return courtRoles
      .slice()
      .sort((a, b) => a.CourtRole.localeCompare(b.CourtRole))
      .filter(r => (term ? r.CourtRole.toLowerCase().includes(term) : true));
  }, [courtRoles, search]);

  const isDuplicate = (name: string, ignoreId?: number): boolean =>
    courtRoles.some(
      r =>
        r.CourtRole.toLowerCase() === name.trim().toLowerCase() &&
        r.idCourtRole !== ignoreId
    );

  const handleCreate = async () => {
    const name = newRoleName.trim();
    if (!name || isDuplicate(name)) return;
    try {
      await createCourtRole({
        idCourtRole: -1,
        CourtRole: name,
        activeRole: true,
        RoleCategory: 'authority',
      });
      showAlert({ text: 'Rolle angelegt' });
      setNewRoleName('');
      onChange();
    } catch {
      showAlert({ severity: 'error', text: 'Fehler beim Anlegen' });
    }
  };

  const handleDelete = async (role: ICourtRole) => {
    if (!window.confirm(`Rolle „${role.CourtRole}" löschen?`)) return;
    try {
      await deleteCourtRole(role.idCourtRole);
      showAlert({ text: 'Rolle gelöscht' });
      onChange();
    } catch (e) {
      console.error(e);
      showAlert({
        severity: 'error',
        text: 'Fehler beim Löschen — möglicherweise sind noch Zuordnungen vorhanden.',
      });
    }
  };

  const startEditing = (role: ICourtRole) => {
    setEditingId(role.idCourtRole);
    setEditingValue(role.CourtRole);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingValue('');
  };

  const saveEditing = async (role: ICourtRole) => {
    const next = editingValue.trim();
    if (!next || next === role.CourtRole) {
      cancelEditing();
      return;
    }
    if (isDuplicate(next, role.idCourtRole)) {
      showAlert({ severity: 'error', text: 'Diese Rolle existiert bereits.' });
      return;
    }
    try {
      await updateCourtRole({ ...role, CourtRole: next });
      showAlert({ text: 'Rolle aktualisiert' });
      cancelEditing();
      onChange();
    } catch (e) {
      console.error(e);
      showAlert({ severity: 'error', text: 'Fehler beim Speichern' });
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Gerichtsrollen pflegen
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          size='small'
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity='info' variant='outlined'>
            Diese Rollen (Grundbuchamt, Finanzamt, Amtsgericht …) werden bei
            jeder Gemarkung-/Gericht-Zuordnung verwendet und sind auch die
            Grundlage für die Maßnahmen-Pflege.
          </Alert>

          {/* Neue Rolle anlegen */}
          <TextField
            label='Neue Rolle anlegen'
            value={newRoleName}
            onChange={e => setNewRoleName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') void handleCreate();
            }}
            placeholder='z. B. Grundbuchamt'
            error={Boolean(newRoleName.trim()) && isDuplicate(newRoleName)}
            helperText={
              Boolean(newRoleName.trim()) && isDuplicate(newRoleName)
                ? 'Diese Rolle existiert bereits.'
                : 'Mit Enter oder Button anlegen.'
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <Button
                    size='small'
                    variant='contained'
                    startIcon={<Add />}
                    onClick={() => void handleCreate()}
                    disabled={
                      !newRoleName.trim() ||
                      isDuplicate(newRoleName) ||
                      isPendingCreateCourtRole
                    }
                  >
                    Anlegen
                  </Button>
                </InputAdornment>
              ),
            }}
          />

          {/* Liste mit Suche + Inline-Edit */}
          <TextField
            size='small'
            label='Rollen suchen'
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              maxHeight: 360,
              overflowY: 'auto',
            }}
          >
            {filtered.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography variant='body2' color='text.secondary'>
                  Keine Rollen gefunden.
                </Typography>
              </Box>
            ) : (
              <List dense disablePadding>
                {filtered.map(r => {
                  const isEditing = editingId === r.idCourtRole;
                  return (
                    <ListItem key={r.idCourtRole} divider>
                      {isEditing ? (
                        <TextField
                          size='small'
                          fullWidth
                          autoFocus
                          value={editingValue}
                          onChange={e => setEditingValue(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') void saveEditing(r);
                            if (e.key === 'Escape') cancelEditing();
                          }}
                        />
                      ) : (
                        <ListItemText primary={r.CourtRole} />
                      )}
                      <ListItemSecondaryAction>
                        {isEditing ? (
                          <>
                            <Button
                              size='small'
                              onClick={() => void saveEditing(r)}
                              disabled={isPendingUpdateCourtRole}
                            >
                              Speichern
                            </Button>
                            <Button size='small' onClick={cancelEditing}>
                              Abbruch
                            </Button>
                          </>
                        ) : (
                          <>
                            <Tooltip title='Umbenennen'>
                              <IconButton
                                size='small'
                                onClick={() => startEditing(r)}
                              >
                                <Edit fontSize='small' />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title='Löschen'>
                              <IconButton
                                size='small'
                                onClick={() => void handleDelete(r)}
                                disabled={isPendingDeleteCourtRole}
                              >
                                <Delete fontSize='small' />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};
